import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  HStack,
  Img,
  Skeleton,
  InputRightElement,
  Input,
  InputGroup,
  Image,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Portal,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { AiOutlineMore } from "react-icons/ai";
import DatePicker from "react-datepicker";
import ReactPaginate from "react-paginate";
import chevronLeft from "../../assets/images/employee/chevron-left.svg";
import chevronRight from "../../assets/images/employee/chevron-right.svg";
import calendarIcon from "../../assets/images/onleave/calendar.svg";
import iconDetail from "../../assets/images/employee/detail.svg";
import SearchableSelect from "../../components/selectForm";
import useDebounce from "../../hooks/useDebounce";
import { useReferralEarning } from "../../hooks/useReferralEarning";
import FormInfo from "../../components/form/formInfo";
import { employeeCall } from "../../api/employee";
import PopupReferralDetail from "./popupReferralDetail";
import ReferralEarningStats from "./referralEarningStats";

// Chương trình thưởng giới thiệu áp dụng từ năm 2026
const START_YEAR = 2026;

const scopeOptions = [
  { key: "month", value: "Theo tháng" },
  { key: "year", value: "Theo năm" },
];

// Lọc theo nguồn của người được giới thiệu (NONE: HCNS chưa chọn nguồn)
const sourceOptions = [
  { key: "COMPANY", value: "Nguồn công ty" },
  { key: "PERSONAL", value: "Nguồn cá nhân" },
  { key: "NONE", value: "Chưa có nguồn" },
];

// Bấm vào các cột này để xem thông tin nhân viên, giống trang Quản lý nhân viên
const EMPLOYEE_INFO_KEYS = ["code", "name"];

const headers = [
  { label: "", key: "action", w: "4%", align: "center" },
  { label: "STT", key: "", w: "4%", align: "center" },
  { label: "Mã NV", key: "code", w: "9%", align: "start" },
  { label: "Tên NV", key: "name", w: "15%", align: "start" },
  { label: "Số người giới thiệu", key: "referredCount", w: "10%", align: "center" },
  { label: "Đã duyệt", key: "approvedCount", w: "8%", align: "center" },
  { label: "Chưa duyệt", key: "noSourceCount", w: "8%", align: "center" },
  { label: "Nguồn công ty", key: "companySourceCount", w: "9%", align: "center" },
  { label: "Nguồn cá nhân", key: "personalSourceCount", w: "9%", align: "center" },
  { label: "Tạm tính", key: "provisionalAmount", w: "12%", align: "end" },
  { label: "Thực nhận", key: "paidAmount", w: "12%", align: "end" },
];

const TableReferralEarning = () => {
  const currentYear = new Date().getFullYear();

  const [search, setSearch] = useState("");
  const [scope, setScope] = useState("month");
  const [monthDate, setMonthDate] = useState(new Date());
  const [year, setYear] = useState(currentYear);
  const [referralSource, setReferralSource] = useState(null);
  const [referredId, setReferredId] = useState(null);
  const [zIndex, setZIndex] = useState(1);
  const [dataDetail, setDataDetail] = useState(null);
  const [dataInfo, setDataInfo] = useState(null);

  const debouncedSearchTerm = useDebounce(search, 500);
  const onChangeSearch = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  const yearOptions = useMemo(() => {
    const opts = [];
    for (let y = currentYear; y >= START_YEAR; y -= 1) {
      opts.push({ key: y, value: String(y) });
    }
    return opts;
  }, [currentYear]);

  const {
    referralEarning,
    summary,
    referredOptions,
    refetch,
    isLoading,
    isRefetching,
    totalPages,
    setCurrentPage,
    currentPage,
    entries,
    startEntry,
    endEntry,
  } = useReferralEarning(
    debouncedSearchTerm,
    scope,
    monthDate,
    year,
    referralSource,
    referredId,
  );

  // Đổi bộ lọc thì quay về trang 1
  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearchTerm,
    scope,
    monthDate,
    year,
    referralSource,
    referredId,
    setCurrentPage,
  ]);

  // Kỳ + bộ lọc đang xem, hiển thị ở popup chi tiết
  const viewMonth = monthDate || new Date();
  // Ô chọn người được giới thiệu: hiện tên, ô tìm kiếm tìm được cả theo mã
  const referredSelectOptions = useMemo(
    () =>
      (referredOptions || []).map((item) => ({
        key: item.id,
        value: item.fullName,
        code: item.code,
      })),
    [referredOptions],
  );
  const selectedReferred = (referredOptions || []).find(
    (item) => item.id === referredId,
  );

  const filterLabel = [
    scope === "year"
      ? `Năm ${year}`
      : `Tháng ${String(viewMonth.getMonth() + 1).padStart(2, "0")}/${viewMonth.getFullYear()}`,
    sourceOptions.find((item) => item.key === referralSource)?.value,
    selectedReferred &&
      `Người được giới thiệu: ${selectedReferred.fullName} (${selectedReferred.code})`,
  ]
    .filter(Boolean)
    .join(" · ");

  // Dòng đang xem chi tiết: lấy bản mới nhất trong bảng để số liệu ở popup
  // cập nhật theo sau khi duyệt nguồn
  const detailRow =
    referralEarning?.find((item) => item.code === dataDetail?.code) ||
    dataDetail;

  // Thông tin nhân viên: lấy đủ hồ sơ theo id rồi mở FormInfo như trang Quản lý nhân viên
  const handleOpenEmployeeInfo = async (id) => {
    if (!id) return;
    try {
      const employee = await employeeCall.get(id);
      if (employee) setDataInfo(employee);
    } catch (error) {
      // employeeCall.get đã báo lỗi bằng toast
    }
  };

  const handlePageChange = (selectedPage) => {
    setCurrentPage(selectedPage.selected + 1);
    refetch();
  };

  return (
    <>
      <Flex alignItems="center" justifyContent="space-between">
        <Flex alignItems="center" gap={{ base: "74px" }}>
          <Text fontSize={{ base: "20px" }} fontWeight={600}>
            Quản lý thưởng giới thiệu
          </Text>
        </Flex>
      </Flex>

      <Box mt={{ base: "23px" }}>
        <Flex flexDirection={"column"} gap={{ base: "16px" }}>
          {/* Ô thống kê: không dùng cả khối thì comment dòng dưới */}
          <ReferralEarningStats summary={summary} />
          <Flex
            backgroundColor="var(--color-main)"
            borderRadius={{ base: "12px" }}
            flexDirection={"column"}
          >
            <Flex
              alignItems={"center"}
              justifyContent={"space-between"}
              gap={{ base: "32px" }}
              p={"16px 16px 12px 16px"}
            >
              <Flex
                alignItems={"flex-start"}
                gap={{ base: "16px 32px" }}
                flex={"1"}
                flexDirection={"column"}
              >
                <InputGroup
                  maxW="42vw"
                  minW={"42vw"}
                  borderRadius={{ base: "8px" }}
                  w={{ base: "100%" }}
                >
                  <Input
                    placeholder="Tên NV, mã NV người giới thiệu, ... "
                    type="text"
                    borderRadius={{ base: "8px" }}
                    border={{ base: "1px solid var(--color-boder)" }}
                    backgroundColor={"var(--fill-avatar)"}
                    onChange={onChangeSearch}
                  />
                  <InputRightElement
                    borderTopRightRadius={"8px"}
                    borderBottomRightRadius={"8px"}
                    pointerEvents="none"
                    cursor={"pointer"}
                    backgroundImage="var(--color-button)"
                  >
                    <SearchIcon color="var(--color-main)" />
                  </InputRightElement>
                </InputGroup>
                <Flex
                  alignItems={"center"}
                  gap={{ base: "16px 32px" }}
                  flex={"1"}
                  flexWrap={"wrap"}
                  w={"100%"}
                >
                  <SearchableSelect
                    isShowSearch={false}
                    options={scopeOptions}
                    initialSelectedOption={scopeOptions[0]}
                    setValue={setScope}
                  />
                  {scope === "month" && (
                    <InputGroup w={"100%"} maxW={"255px"} zIndex={zIndex}>
                      <DatePicker
                        selected={monthDate}
                        onChange={(d) => setMonthDate(d)}
                        showMonthYearPicker
                        dateFormat="MM/yyyy"
                        locale="vi"
                        isClearable
                        placeholderText="Chọn tháng"
                        className="datePickerStyle"
                        onFocus={() => setZIndex(3)}
                        onBlur={() => setZIndex(1)}
                      />
                      {!monthDate && (
                        <InputRightElement pointerEvents="none" h={"44px"}>
                          <Image src={calendarIcon} />
                        </InputRightElement>
                      )}
                    </InputGroup>
                  )}
                  {scope === "year" && (
                    <SearchableSelect
                      isShowSearch={false}
                      options={yearOptions}
                      initialSelectedOption={{
                        key: currentYear,
                        value: String(currentYear),
                      }}
                      selectedKey={year}
                      setValue={setYear}
                    />
                  )}
                  <SearchableSelect
                    isShowSearch={false}
                    options={sourceOptions}
                    initialSelectedOption={{
                      key: 0,
                      value: "Chọn nguồn giới thiệu",
                    }}
                    setValue={setReferralSource}
                  />
                  <SearchableSelect
                    isShowSearch={true}
                    options={referredSelectOptions}
                    initialSelectedOption={{
                      key: 0,
                      value: "Chọn người được giới thiệu",
                    }}
                    setValue={setReferredId}
                  />
                </Flex>
              </Flex>
            </Flex>
            <Box width={"100%"} height={"100%"}>
              <Skeleton isLoaded={!isLoading && !isRefetching}>
                <TableContainer
                  w={"100%"}
                  maxH="calc(100vh - 350.5px)"
                  overflowY="auto"
                >
                  <Table w={"100%"} variant="unstyled">
                    <Thead
                      position="sticky"
                      top="0"
                      zIndex="1"
                      w={"100%"}
                      h={"41px"}
                      color="white"
                      fontWeight="400"
                      backgroundColor="#F9FAFD"
                    >
                      <Tr h={{ base: "41px" }}>
                        {headers.map((e, index) => (
                          <Td
                            p={"16px 16px 8px 16px"}
                            key={index}
                            border={"none"}
                            color={"#51607B"}
                            fontSize={{ base: "14px" }}
                            w={e.w}
                          >
                            <Box textAlign={e.align} fontWeight={"bold"}>
                              {e.label}
                            </Box>
                          </Td>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody w={"100%"} h={"100%"}>
                      {referralEarning?.length > 0 ? (
                        referralEarning.map((row, rowIndex) => (
                          <Tr key={row.id || rowIndex} h={"72px"}>
                            {headers.map((e, index) => (
                              <Td
                                p={"16px 16px 8px 16px"}
                                w={e.w}
                                key={index}
                                bg={
                                  rowIndex % 2 === 0
                                    ? "var(--color-main)"
                                    : "var(--fill-avatar)"
                                }
                              >
                                <Box
                                  fontSize={{ base: "14px" }}
                                  lineHeight={"19.6px"}
                                  textAlign={e.align}
                                  fontWeight={500}
                                  color={"var(--color-info-employee)"}
                                >
                                  {e.key === "action" ? (
                                    <Menu>
                                      <MenuButton
                                        _hover={{ backgroundColor: "none" }}
                                        _active={{ backgroundColor: "none" }}
                                        background="none"
                                        as={Button}
                                      >
                                        <AiOutlineMore fontSize={"20px"} />
                                      </MenuButton>
                                      {/* Portal để menu không bị bảng (overflow) cắt mất khi ít dòng */}
                                      <Portal>
                                        <MenuList
                                          fontSize={{ base: "14px" }}
                                          fontWeight={500}
                                          lineHeight={"140%"}
                                          color={"var(--color-info-employee)"}
                                        >
                                          <MenuItem
                                            gap={"16px"}
                                            onClick={() => setDataDetail(row)}
                                          >
                                            <Image src={iconDetail} /> Xem chi tiết
                                          </MenuItem>
                                        </MenuList>
                                      </Portal>
                                    </Menu>
                                  ) : EMPLOYEE_INFO_KEYS.includes(e.key) ? (
                                    <Text
                                      cursor={"pointer"}
                                      _hover={{
                                        color:
                                          "var(--color-option-employee-hover)",
                                      }}
                                      onClick={() =>
                                        handleOpenEmployeeInfo(row.id)
                                      }
                                    >
                                      {row[e.key]}
                                    </Text>
                                  ) : (
                                    <Text>
                                      {e.key === ""
                                        ? (startEntry || 1) + rowIndex
                                        : row[e.key]}
                                    </Text>
                                  )}
                                </Box>
                              </Td>
                            ))}
                          </Tr>
                        ))
                      ) : (
                        <Tr>
                          <Td
                            colSpan={headers.length}
                            textAlign="center"
                            padding={"40px 0"}
                          >
                            Không có dữ liệu
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Skeleton>
              <Box mt={10} mr={5} mb={5} ml={5}>
                <Flex alignItems={"center"} justifyContent={"space-between"}>
                  <Text>
                    Showing {startEntry} to {endEntry} of {entries} entries
                  </Text>
                  <HStack spacing={2} justify="flex-end">
                    <ReactPaginate
                      className="pagination-custom"
                      pageCount={totalPages}
                      pageRangeDisplayed={3}
                      marginPagesDisplayed={1}
                      onPageChange={handlePageChange}
                      containerClassName={"pagination"}
                      activeClassName={"active"}
                      breakClassName={"ellipsis"}
                      breakLabel={"..."}
                      previousLabel={
                        <Img
                          w={"16px"}
                          minW={"16px"}
                          h={"16px"}
                          src={chevronLeft}
                        />
                      }
                      nextLabel={
                        <Img
                          w={"16px"}
                          minW={"16px"}
                          h={"16px"}
                          src={chevronRight}
                        />
                      }
                      renderOnZeroPageCount={null}
                      forcePage={currentPage - 1}
                    />
                  </HStack>
                </Flex>
              </Box>
            </Box>
          </Flex>
        </Flex>
      </Box>
      {dataDetail && (
        <PopupReferralDetail
          isOpen={!!dataDetail}
          onClose={() => setDataDetail(null)}
          data={detailRow}
          filterLabel={filterLabel}
          scope={scope}
          monthDate={monthDate}
          year={year}
          referralSource={referralSource}
          referredId={referredId}
        />
      )}
      {dataInfo && (
        <FormInfo
          isOpen={!!dataInfo}
          onClose={() => setDataInfo(null)}
          defaultValues={dataInfo}
        />
      )}
    </>
  );
};

export default TableReferralEarning;
